import { TimePicker } from '@components/TimePicker';
import {
  Box,
  DatePicker,
  Grid,
  Stack,
  TextField,
  CollapsibleCard,
} from '@atgfr8/atg-ui-tailwind';
import { Button, Notification, SelectInput, ToastType } from '@components';
import { SelectInputCustom } from '@components/Select';
import { createForm } from '@felte/solid';
import {
  EDIStatusCodesApiResponse,
  fetchEDIStatusCodes,
  orderStore,
  sendEDIUpdate,
} from '@store/orders';
import { customerStore } from '@store/orders/store';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { sanitizeStateInput } from '@utils/formUtils';
import { getEligibleEntries, handleToast } from '@utils/utils';
import { find, isEmpty, size } from 'lodash';
import { DateTime } from 'luxon';
import { Component, Show, createEffect, createSignal, onMount } from 'solid-js';

import { mapCodesToMenuItems } from './helper';

const EDI_TIMEZONE_MENU_ITEMS = [
  { label: 'Eastern', value: 'EST' },
  { label: 'Central', value: 'CST' },
  { label: 'Mountain', value: 'MST' },
  { label: 'Pacific', value: 'PST' },
];

const getDefaultEdiTimezone = (timezone: string | null): string =>
  timezone !== null &&
  EDI_TIMEZONE_MENU_ITEMS.some((menuItem) => menuItem.value === timezone)
    ? timezone
    : 'EST';

type Props = {
  load: ILoadEditViewModel;
  index: number;
};

type FormValues = {
  appointmentStatus: string | null;
  date: string;
  note: string;
  shipmentStatus: string;
  statusReason: string;
  stopId: number;
  timezone: string;
  time: string;
  appointmentNumber: string;
  city: string | null;
  state: string | null;
  latitude: string | null;
  longitude: string | null;
  temperature: string | null;
};

const SendEDIUpdate: Component<Props> = (props: Props) => {
  const [statusCodes, setStatusCodes] = createSignal<EDIStatusCodesApiResponse>(
    {
      statusCodes: [],
      statusReasons: [],
      appointmentCodes: [],
    },
  );

  const getStopLocation = () => {
    const eligibleStops = getEligibleEntries<LastUsedTopStopModel>(
      props.load.stops,
    );
    return size(eligibleStops) > 0
      ? eligibleStops
          .filter((stop) => stop.id)
          .map((stop) => ({
            label: stop.locationName,
            value: Number(stop.id),
          }))
      : [];
  };

  const handleSubmit = async function (values: FormValues) {
    const {
      appointmentStatus,
      appointmentNumber,
      note,
      shipmentStatus,
      statusReason,
      stopId,
      timezone,
      date,
      time,
      latitude,
      longitude,
      city,
      state,
      temperature,
    } = values;

    if (shipmentStatus === '') {
      handleToast(ToastType.Error, 'No Update Event has been selected');
      return;
    }

    if (
      x6Selected() &&
      !(
        (!isEmpty(data().city) && !isEmpty(data().state)) ||
        (!isEmpty(data().latitude) && !isEmpty(data().longitude))
      )
    ) {
      handleToast(
        ToastType.Error,
        'To send an (X6) En Route update, you need to enter either a City and State, or, Latitude and Longitude.',
      );
      return;
    }

    if (
      !DateTime.fromISO(date).isValid ||
      !DateTime.fromISO(time).isValid ||
      DateTime.fromISO(date).year < 2000
    ) {
      handleToast(ToastType.Error, 'Date and Time must be valid values.');
      return;
    }

    const payload = {
      appointmentStatus,
      appointmentNumber,
      note,
      shipmentStatus,
      statusReason,
      stopId,
      timezone,
      date: `${DateTime.fromISO(date).toFormat(
        'MM/dd/yyyy',
      )} ${DateTime.fromISO(time).toFormat('HH:mm a')}`,
      loadId: props.load.id,
      city,
      state,
      latitude,
      longitude,
      temperature,
    };

    try {
      await sendEDIUpdate(props.load.id, payload);
      handleToast(ToastType.Success, 'EDI Update Sent Successfully');
      reset();
      setFields(
        'timezone',
        getDefaultEdiTimezone(customerStore.customer.defaultEdiTimeZone),
      );
    } catch (error) {
      handleToast(ToastType.Error, (error as Error).message);
    }
  };

  const { form, data, setFields, reset } = createForm<FormValues>({
    initialValues: {
      appointmentStatus: null,
      date: DateTime.local().toISO({
        includeOffset: false,
        suppressMilliseconds: true,
      }),
      note: '',
      shipmentStatus: '',
      statusReason: 'NS',
      stopId: getStopLocation()[0]?.value ?? 0,
      timezone: 'EST',
      time: DateTime.local().toISO({
        includeOffset: false,
        suppressMilliseconds: true,
      }),
      appointmentNumber: '',
    },
  });

  createEffect(() => {
    setFields(
      'timezone',
      getDefaultEdiTimezone(customerStore.customer.defaultEdiTimeZone),
    );
  });

  const fetchEdiStatusCodes = async () => {
    if (orderStore.order.customerId === null) return;
    try {
      const res = await fetchEDIStatusCodes(orderStore.order.customerId);
      setStatusCodes(res);
    } catch (error) {
      handleToast(ToastType.Error, 'Error fetching EDI status codes');
    }
  };

  const getEventMenuItems = () => [
    ...mapCodesToMenuItems(statusCodes().statusCodes),
    ...mapCodesToMenuItems(statusCodes().appointmentCodes).map((i) => ({
      ...i,
      category: 'Appointment',
    })),
  ];

  const showAppointment = () =>
    find(statusCodes().appointmentCodes, { code: data().shipmentStatus });

  const x6Selected = () => data().shipmentStatus.includes('X6');

  onMount(async () => {
    if (orderStore.order.edi) await fetchEdiStatusCodes();
  });

  return (
    <form ref={form}>
      <CollapsibleCard
        class="mb-2"
        startTitle="Send EDI Update"
        endIcon={
          <Button
            disabled={
              !orderStore.order.edi &&
              orderStore.order.loadTenderId === null &&
              !orderStore.order.show214
            }
            onClick={async () => {
              await handleSubmit(data());
            }}
            type="button"
            label="Send Update"
            sx={{ background: '#468DB5', color: '#FFF' }}
            dataTestId={formatDataTestId('Send Update', 'button')}
          />
        }
      >
        <Stack spacing={2}>
          <Notification
            type="info"
            text="This is the alternative to logging into your customer portal to log shipment updates. Communication is not instantaneous, there is a transit time."
          />
          <Show
            when={
              orderStore.order.loadTenderId !== null ||
              (orderStore.order.edi && orderStore.order.show214)
            }
          >
            <Box style={{ 'flex-grow': 1 }}>
              <Grid container spacing={4}>
                <Grid item size={{ xs: 12, sm: 4 }}>
                  <SelectInput
                    label="Stop"
                    menuItems={getStopLocation().map((stop) => ({
                      label: stop.label ?? '',
                      value: String(stop.value),
                    }))}
                    value={String(data().stopId)}
                    onChange={(value) => setFields('stopId', Number(value))}
                    placeholder="Select Stop"
                    dataTestId={formatDataTestId('Stop', 'selectInput')}
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 4 }}>
                  <SelectInputCustom
                    label="Event"
                    menuItems={getEventMenuItems()}
                    value={data().shipmentStatus}
                    onChange={(value) => {
                      if (!value.includes('X6')) {
                        setFields('city', null);
                        setFields('state', null);
                        setFields('latitude', null);
                        setFields('longitude', null);
                        setFields('temperature', null);
                      }
                      setFields('shipmentStatus', value);
                    }}
                    placeholder="Select Event"
                    dataTestId={formatDataTestId('Event', 'selectInput')}
                    menuClass="max-h-[120px]"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 4 }}>
                  <SelectInput
                    label="Reason"
                    menuItems={mapCodesToMenuItems(
                      statusCodes().statusReasons,
                    ).map((item) => ({
                      label: item.label,
                      value: String(item.value),
                    }))}
                    value={data().statusReason}
                    onChange={(value) => setFields('statusReason', value)}
                    placeholder="Select Reason"
                    dataTestId={formatDataTestId('Reason', 'selectInput')}
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 2 }}>
                  <DatePicker
                    label="When"
                    value={data().date}
                    handleChange={(val) => {
                      setFields('date', val);
                    }}
                    class="[&>.MuiFormControl-root]:p-0!"
                    placeholder="Select Date"
                    dataTestId={'when-datepicker'}
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 2 }}>
                  <TimePicker
                    label="Time"
                    value={data().time}
                    onChange={(newTime) => {
                      setFields(
                        'time',
                        newTime.includes('T:00') ? '' : newTime,
                      );
                    }}
                    dataTestId={'time-timepicker'}
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 2 }}>
                  <SelectInput
                    label="Timezone"
                    menuItems={EDI_TIMEZONE_MENU_ITEMS}
                    value={data().timezone}
                    onChange={(value) => setFields('timezone', value)}
                    dataTestId={formatDataTestId('Timezone', 'selectInput')}
                  />
                </Grid>
                <Show
                  when={showAppointment() !== undefined}
                  fallback={
                    <Grid item size={{ xs: 12, sm: 4 }}>
                      {''}
                    </Grid>
                  }
                >
                  <Grid item size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Appointment #"
                      name="appointmentStatus"
                      placeholder="Enter appointment #"
                      dataTestId={'appointment-number-textInput'}
                    />
                  </Grid>
                </Show>
                <Show when={x6Selected()}>
                  <Grid item size={{ sm: 3 }}>
                    <TextField
                      label="City"
                      name="city"
                      placeholder="City"
                      dataTestId={'city-textInput'}
                    />
                  </Grid>
                  <Grid item size={{ sm: 3 }}>
                    <TextField
                      label="State"
                      name="state"
                      placeholder="State"
                      dataTestId={'state-textInput'}
                      maxLength={2}
                      onChange={(value: string) => {
                        const sanitized = sanitizeStateInput(value);
                        if (sanitized === (data().state ?? '')) return;
                        setFields('state', sanitized);
                      }}
                    />
                  </Grid>
                  <Grid item size={{ sm: 2 }}>
                    <TextField
                      label="Latitude"
                      name="latitude"
                      placeholder="Latitude"
                      dataTestId={'latitude-textInput'}
                    />
                  </Grid>
                  <Grid item size={{ sm: 2 }}>
                    <TextField
                      label="Longitude"
                      name="longitude"
                      placeholder="Longitude"
                      dataTestId={'longitude-textInput'}
                    />
                  </Grid>
                  <Grid item size={{ sm: 2 }}>
                    <TextField
                      label="Temperature"
                      name="temperature"
                      placeholder="Temperature"
                      dataTestId={'temperature-textInput'}
                    />
                  </Grid>
                </Show>
                <Grid item size={12}>
                  <TextField
                    label="Note"
                    multiline
                    rows={2}
                    name="note"
                    placeholder="Enter notes here"
                    dataTestId={'note-textInput'}
                  />
                </Grid>
              </Grid>
            </Box>
          </Show>
          <span></span>
          <Show
            when={
              !orderStore.order.edi &&
              orderStore.order.loadTenderId === null &&
              !orderStore.order.show214
            }
          >
            <Notification
              type="warning"
              text="No Load Tender is attached. Cannot send EDI Updates without a Load Tender"
            />
          </Show>
        </Stack>
      </CollapsibleCard>
    </form>
  );
};

export default SendEDIUpdate;
